import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  OrganizationSsoConnectionAdmin,
  OrganizationSsoConnectionWrite,
  OrganizationSsoIdentifierType,
  OrganizationSsoKind,
  OrganizationSsoMode,
  TenantOrganizationSsoConfig,
} from '../../../../shared-types/src/index.js';
import { describeError } from '../../errors.js';
import type { AdminHttpClient } from '../../http.js';
import type { ToastMessage } from '../../types.js';

const EMPTY_FORM = {
  connectionKey: '',
  kind: 'entra_oidc' as OrganizationSsoKind,
  displayName: '',
  serviceBaseUrl: '',
  directoryTenantId: '',
  clientId: '',
  clientAuthMethod: 'client_secret_post',
  secret: '',
  privateKey: '',
  idpMetadataUrl: '',
  idpEntityId: '',
  idpSsoUrl: '',
  idpSigningCertificates: '',
  spEntityId: '',
  spPublicCertificates: '',
  verifyUrl: '',
  verificationAuthMode: 'bearer',
  clientCertificate: '',
  allowedIdentifierTypes: 'email',
  identifierFieldName: '',
  responseMapping: '{}',
  timeoutMs: '5000',
  claimMapping: '{}',
  allowedEmailDomains: '',
  provisioningMode: 'invite_only' as 'invite_only' | 'jit_restricted',
};

type FormState = typeof EMPTY_FORM;

export function OrganizationSsoTab({
  http,
  tenantId,
  onToast,
}: {
  http: AdminHttpClient;
  tenantId: string;
  onToast: (toast: ToastMessage) => void;
}) {
  const { t } = useTranslation();
  const [config, setConfig] = useState<TenantOrganizationSsoConfig | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string>();
  const [testIdentifiers, setTestIdentifiers] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!tenantId) {
      setConfig(null);
      return;
    }
    try {
      setConfig(await http.getOrganizationSsoConfig(tenantId));
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, t) });
    }
  }, [http, onToast, t, tenantId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!form.serviceBaseUrl && typeof window !== 'undefined')
      setForm((current) => ({ ...current, serviceBaseUrl: window.location.origin }));
  }, [form.serviceBaseUrl]);

  if (!tenantId) return <p className="screen-help">{t('platform.selectTenant')}</p>;

  const mode = config?.organizationSsoMode ?? 'disabled';

  function field<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function changeMode(next: OrganizationSsoMode) {
    const confirmBreakGlass =
      next === 'required' ? window.confirm(t('organizationSso.requiredConfirm')) : undefined;
    if (next === 'required' && !confirmBreakGlass) return;
    await act(async () => {
      setConfig(await http.updateOrganizationSsoMode(tenantId, next, confirmBreakGlass));
    });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    await act(async () => {
      const connection = buildConnection(form);
      if (editingId) {
        await http.updateOrganizationSsoConnection(tenantId, editingId, {
          displayName: connection.displayName,
          config: connection.config,
          ...(connection.secret ? { secret: connection.secret } : {}),
          ...(connection.privateKey ? { privateKey: connection.privateKey } : {}),
          ...(connection.clientCertificate
            ? { clientCertificate: connection.clientCertificate }
            : {}),
          claimMapping: connection.claimMapping,
          provisioningMode: connection.provisioningMode,
          allowedEmailDomains: connection.allowedEmailDomains,
        });
      } else {
        await http.createOrganizationSsoConnection(tenantId, connection);
      }
      setEditingId(undefined);
      setForm({
        ...EMPTY_FORM,
        serviceBaseUrl: form.serviceBaseUrl,
      });
      await reload();
    });
  }

  async function test(connection: OrganizationSsoConnectionAdmin) {
    const identifier = testIdentifiers[connection.id]?.trim();
    const identifierType = (
      Array.isArray(connection.config.allowedIdentifierTypes)
        ? connection.config.allowedIdentifierTypes[0]
        : 'email'
    ) as OrganizationSsoIdentifierType;
    await act(async () => {
      const result = await http.testOrganizationSsoConnection(
        tenantId,
        connection.id,
        connection.kind === 'verification_api' ? { identifierType, identifier } : {}
      );
      onToast({
        tone: result.ok ? 'success' : 'error',
        message: result.ok
          ? t('organizationSso.testPassed')
          : `${t('organizationSso.testFailed')}: ${result.errorCode ?? ''}`,
      });
      await reload();
    }, false);
  }

  async function toggle(connection: OrganizationSsoConnectionAdmin) {
    await act(async () => {
      await http.setOrganizationSsoConnectionEnabled(tenantId, connection.id, !connection.enabled);
      await reload();
    });
  }

  async function remove(connection: OrganizationSsoConnectionAdmin) {
    if (!window.confirm(t('organizationSso.deleteConfirm', { name: connection.displayName })))
      return;
    await act(async () => {
      await http.deleteOrganizationSsoConnection(tenantId, connection.id);
      await reload();
    });
  }

  async function downloadMetadata(connection: OrganizationSsoConnectionAdmin) {
    await act(async () => {
      const xml = await http.getOrganizationSsoSamlMetadata(tenantId, connection.id);
      const href = URL.createObjectURL(new Blob([xml], { type: 'application/samlmetadata+xml' }));
      const link = document.createElement('a');
      link.href = href;
      link.download = `${connection.connectionKey}-sp-metadata.xml`;
      link.click();
      URL.revokeObjectURL(href);
    }, false);
  }

  function edit(connection: OrganizationSsoConnectionAdmin) {
    const value = connection.config;
    setEditingId(connection.id);
    setForm({
      ...EMPTY_FORM,
      connectionKey: connection.connectionKey,
      kind: connection.kind,
      displayName: connection.displayName,
      serviceBaseUrl: serviceBaseUrl(value, connection.id),
      directoryTenantId: stringValue(value.directoryTenantId),
      clientId: stringValue(value.clientId),
      clientAuthMethod: stringValue(value.clientAuthMethod) || 'client_secret_post',
      idpMetadataUrl: stringValue(value.idpMetadataUrl),
      idpEntityId: stringValue(value.idpEntityId),
      idpSsoUrl: stringValue(value.idpSsoUrl),
      idpSigningCertificates: stringList(value.idpSigningCertificates).join('\n\n'),
      spEntityId: stringValue(value.spEntityId),
      spPublicCertificates: stringList(value.spPublicCertificates).join('\n\n'),
      verifyUrl: stringValue(value.verifyUrl),
      verificationAuthMode: stringValue(value.authMode) || 'bearer',
      allowedIdentifierTypes: stringList(value.allowedIdentifierTypes).join(', '),
      identifierFieldName: stringValue(value.identifierFieldName),
      responseMapping: JSON.stringify(value.responseMapping ?? {}, null, 2),
      timeoutMs: String(value.timeoutMs ?? 5000),
      claimMapping: JSON.stringify(connection.claimMapping, null, 2),
      allowedEmailDomains: connection.allowedEmailDomains.join(', '),
      provisioningMode: connection.provisioningMode,
      secret: '',
      privateKey: '',
      clientCertificate: '',
    });
  }

  async function act(run: () => Promise<void>, toast = true) {
    setBusy(true);
    try {
      await run();
      if (toast) onToast({ tone: 'success', message: t('common.changed') });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, t) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="content-stack">
      <section className="panel form-grid">
        <h2>{t('organizationSso.title')}</h2>
        <label>
          {t('organizationSso.mode')}
          <select
            value={mode}
            disabled={busy}
            onChange={(event) => void changeMode(event.target.value as OrganizationSsoMode)}
          >
            <option value="disabled">{t('organizationSso.modeDisabled')}</option>
            <option value="optional">{t('organizationSso.modeOptional')}</option>
            <option value="required">{t('organizationSso.modeRequired')}</option>
          </select>
        </label>
        <p className="screen-help">{t('organizationSso.modeHelp')}</p>
      </section>

      <section className="panel">
        <h3>{t('organizationSso.connections')}</h3>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('organizationSso.displayName')}</th>
                <th>{t('organizationSso.kind')}</th>
                <th>{t('organizationSso.status')}</th>
                <th>{t('organizationSso.lastTest')}</th>
                <th>{t('organizationSso.lastLogin')}</th>
                <th>{t('organizationSso.identities')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {(config?.connections ?? []).map((connection) => (
                <tr key={connection.id}>
                  <td>{connection.displayName}</td>
                  <td>{t(`organizationSso.kind_${connection.kind}`)}</td>
                  <td>
                    <span className="badge">
                      {t(`organizationSso.status_${connection.status}`)}
                    </span>
                  </td>
                  <td>
                    {connection.lastTestAt ? new Date(connection.lastTestAt).toLocaleString() : '—'}
                  </td>
                  <td>
                    {connection.lastSuccessAt
                      ? new Date(connection.lastSuccessAt).toLocaleString()
                      : '—'}
                  </td>
                  <td>{connection.identityCount}</td>
                  <td>
                    <div className="button-row">
                      {connection.kind === 'verification_api' && (
                        <input
                          aria-label={t('organizationSso.testIdentifier')}
                          value={testIdentifiers[connection.id] ?? ''}
                          onChange={(event) =>
                            setTestIdentifiers((current) => ({
                              ...current,
                              [connection.id]: event.target.value,
                            }))
                          }
                          placeholder={t('organizationSso.testIdentifier')}
                          autoComplete="off"
                        />
                      )}
                      <button type="button" disabled={busy} onClick={() => void test(connection)}>
                        {t('organizationSso.test')}
                      </button>
                      <button type="button" disabled={busy} onClick={() => void toggle(connection)}>
                        {connection.enabled
                          ? t('organizationSso.disable')
                          : t('organizationSso.enable')}
                      </button>
                      <button type="button" disabled={busy} onClick={() => edit(connection)}>
                        {t('common.edit')}
                      </button>
                      {connection.kind === 'saml2' && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void downloadMetadata(connection)}
                        >
                          {t('organizationSso.metadata')}
                        </button>
                      )}
                      <button type="button" disabled={busy} onClick={() => void remove(connection)}>
                        {t('common.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {(config?.connections.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={7}>{t('organizationSso.empty')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="panel">
        <h3>
          {editingId ? t('organizationSso.editConnection') : t('organizationSso.addConnection')}
        </h3>
        <form className="form-grid" onSubmit={(event) => void submit(event)}>
          <label>
            {t('organizationSso.kind')}
            <select
              value={form.kind}
              disabled={Boolean(editingId)}
              onChange={(event) => field('kind', event.target.value as OrganizationSsoKind)}
            >
              <option value="entra_oidc">{t('organizationSso.kind_entra_oidc')}</option>
              <option value="saml2">{t('organizationSso.kind_saml2')}</option>
              <option value="verification_api">{t('organizationSso.kind_verification_api')}</option>
            </select>
          </label>
          <label>
            {t('organizationSso.connectionKey')}
            <input
              value={form.connectionKey}
              disabled={Boolean(editingId)}
              onChange={(event) => field('connectionKey', event.target.value)}
              pattern="[a-z0-9][a-z0-9_-]{0,63}"
              required
            />
          </label>
          <label>
            {t('organizationSso.displayName')}
            <input
              value={form.displayName}
              onChange={(event) => field('displayName', event.target.value)}
              required
            />
          </label>
          <label>
            {t('organizationSso.serviceBaseUrl')}
            <input
              type="url"
              value={form.serviceBaseUrl}
              onChange={(event) => field('serviceBaseUrl', event.target.value)}
              required
            />
          </label>

          {form.kind === 'entra_oidc' && <EntraFields form={form} field={field} t={t} />}
          {form.kind === 'saml2' && <SamlFields form={form} field={field} t={t} />}
          {form.kind === 'verification_api' && (
            <VerificationFields form={form} field={field} t={t} />
          )}

          <label>
            {t('organizationSso.provisioningMode')}
            <select
              value={form.provisioningMode}
              onChange={(event) =>
                field('provisioningMode', event.target.value as FormState['provisioningMode'])
              }
            >
              <option value="invite_only">{t('organizationSso.inviteOnly')}</option>
              <option value="jit_restricted">{t('organizationSso.jitRestricted')}</option>
            </select>
          </label>
          <label>
            {t('organizationSso.allowedDomains')}
            {/* Required: an IdP-asserted address may only select a Veluga
                account when its domain is allowlisted, so a connection with an
                empty list can never onboard anyone. */}
            <input
              required
              value={form.allowedEmailDomains}
              onChange={(event) => field('allowedEmailDomains', event.target.value)}
              placeholder="example.ac.kr, corp.example"
            />
          </label>
          <label>
            {t('organizationSso.claimMapping')}
            <textarea
              value={form.claimMapping}
              onChange={(event) => field('claimMapping', event.target.value)}
              rows={4}
            />
          </label>
          <div className="button-row">
            <button type="submit" disabled={busy}>
              {editingId ? t('common.save') : t('common.add')}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => {
                  setEditingId(undefined);
                  setForm({ ...EMPTY_FORM, serviceBaseUrl: form.serviceBaseUrl });
                }}
              >
                {t('common.cancel')}
              </button>
            )}
          </div>
        </form>
      </section>
    </section>
  );
}

function EntraFields({ form, field, t }: FieldProps) {
  return (
    <>
      <label>
        {t('organizationSso.directoryTenantId')}
        <input
          value={form.directoryTenantId}
          onChange={(event) => field('directoryTenantId', event.target.value)}
          required
        />
      </label>
      <label>
        {t('organizationSso.clientId')}
        <input
          value={form.clientId}
          onChange={(event) => field('clientId', event.target.value)}
          required
        />
      </label>
      <label>
        {t('organizationSso.clientAuthMethod')}
        <select
          value={form.clientAuthMethod}
          onChange={(event) => field('clientAuthMethod', event.target.value)}
        >
          <option value="client_secret_post">Client secret</option>
          <option value="private_key_jwt">Private key JWT</option>
        </select>
      </label>
      {form.clientAuthMethod === 'client_secret_post' ? (
        <label>
          {t('organizationSso.clientSecret')}
          <input
            type="password"
            value={form.secret}
            onChange={(event) => field('secret', event.target.value)}
            autoComplete="new-password"
          />
        </label>
      ) : (
        <label>
          {t('organizationSso.privateKey')}
          <textarea
            value={form.privateKey}
            onChange={(event) => field('privateKey', event.target.value)}
            rows={6}
          />
        </label>
      )}
      <p className="screen-help">
        {t('organizationSso.redirectPreview', {
          url: `${trimBase(form.serviceBaseUrl)}/v1/auth/organization-sso/{connectionId}/oidc/callback`,
        })}
      </p>
    </>
  );
}

function SamlFields({ form, field, t }: FieldProps) {
  return (
    <>
      <label>
        {t('organizationSso.idpMetadataUrl')}
        <input
          type="url"
          value={form.idpMetadataUrl}
          onChange={(event) => field('idpMetadataUrl', event.target.value)}
        />
      </label>
      <label>
        {t('organizationSso.idpEntityId')}
        <input
          value={form.idpEntityId}
          onChange={(event) => field('idpEntityId', event.target.value)}
          required
        />
      </label>
      <label>
        {t('organizationSso.idpSsoUrl')}
        <input
          type="url"
          value={form.idpSsoUrl}
          onChange={(event) => field('idpSsoUrl', event.target.value)}
          required
        />
      </label>
      <label>
        {t('organizationSso.idpSigningCertificates')}
        <textarea
          value={form.idpSigningCertificates}
          onChange={(event) => field('idpSigningCertificates', event.target.value)}
          rows={7}
          required
        />
      </label>
      <label>
        {t('organizationSso.spEntityId')}
        <input
          value={form.spEntityId}
          onChange={(event) => field('spEntityId', event.target.value)}
          required
        />
      </label>
      <label>
        {t('organizationSso.spPublicCertificates')}
        <textarea
          value={form.spPublicCertificates}
          onChange={(event) => field('spPublicCertificates', event.target.value)}
          rows={7}
          required
        />
      </label>
      <label>
        {t('organizationSso.privateKey')}
        <textarea
          value={form.privateKey}
          onChange={(event) => field('privateKey', event.target.value)}
          rows={7}
        />
      </label>
      <p className="screen-help">
        {t('organizationSso.acsPreview', {
          url: `${trimBase(form.serviceBaseUrl)}/v1/auth/organization-sso/{connectionId}/saml/acs`,
        })}
      </p>
    </>
  );
}

function VerificationFields({ form, field, t }: FieldProps) {
  return (
    <>
      <label>
        {t('organizationSso.verifyUrl')}
        <input
          type="url"
          value={form.verifyUrl}
          onChange={(event) => field('verifyUrl', event.target.value)}
          required
        />
      </label>
      <label>
        {t('organizationSso.authMode')}
        <select
          value={form.verificationAuthMode}
          onChange={(event) => field('verificationAuthMode', event.target.value)}
        >
          <option value="bearer">Bearer</option>
          <option value="mtls">mTLS</option>
        </select>
      </label>
      {form.verificationAuthMode === 'bearer' ? (
        <label>
          {t('organizationSso.bearerSecret')}
          <input
            type="password"
            value={form.secret}
            onChange={(event) => field('secret', event.target.value)}
            autoComplete="new-password"
          />
        </label>
      ) : (
        <>
          <label>
            {t('organizationSso.clientCertificate')}
            <textarea
              value={form.clientCertificate}
              onChange={(event) => field('clientCertificate', event.target.value)}
              rows={6}
            />
          </label>
          <label>
            {t('organizationSso.privateKey')}
            <textarea
              value={form.privateKey}
              onChange={(event) => field('privateKey', event.target.value)}
              rows={6}
            />
          </label>
        </>
      )}
      <label>
        {t('organizationSso.identifierTypes')}
        <input
          value={form.allowedIdentifierTypes}
          onChange={(event) => field('allowedIdentifierTypes', event.target.value)}
          required
        />
      </label>
      <label>
        {t('organizationSso.identifierField')}
        <input
          value={form.identifierFieldName}
          onChange={(event) => field('identifierFieldName', event.target.value)}
        />
      </label>
      <label>
        {t('organizationSso.timeoutMs')}
        <input
          type="number"
          min="1"
          max="15000"
          value={form.timeoutMs}
          onChange={(event) => field('timeoutMs', event.target.value)}
        />
      </label>
      <label>
        {t('organizationSso.responseMapping')}
        <textarea
          value={form.responseMapping}
          onChange={(event) => field('responseMapping', event.target.value)}
          rows={7}
        />
      </label>
    </>
  );
}

interface FieldProps {
  form: FormState;
  field: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  t: ReturnType<typeof useTranslation>['t'];
}

function buildConnection(form: FormState): OrganizationSsoConnectionWrite {
  const serviceBaseUrl = trimBase(form.serviceBaseUrl);
  const common = {
    connectionKey: form.connectionKey.trim(),
    kind: form.kind,
    displayName: form.displayName.trim(),
    ...(form.secret ? { secret: form.secret } : {}),
    ...(form.privateKey ? { privateKey: form.privateKey } : {}),
    ...(form.clientCertificate ? { clientCertificate: form.clientCertificate } : {}),
    claimMapping: parseObject(form.claimMapping),
    provisioningMode: form.provisioningMode,
    allowedEmailDomains: commaList(form.allowedEmailDomains),
  };
  if (form.kind === 'entra_oidc') {
    return {
      ...common,
      config: {
        directoryTenantId: form.directoryTenantId.trim(),
        clientId: form.clientId.trim(),
        redirectUri: `${serviceBaseUrl}/v1/auth/organization-sso/{connectionId}/oidc/callback`,
        expectedIssuer: `https://login.microsoftonline.com/${form.directoryTenantId.trim()}/v2.0`,
        scopes: ['openid', 'profile', 'email'],
        clientAuthMethod: form.clientAuthMethod,
      },
    };
  }
  if (form.kind === 'saml2') {
    return {
      ...common,
      config: {
        ...(form.idpMetadataUrl.trim() ? { idpMetadataUrl: form.idpMetadataUrl.trim() } : {}),
        idpEntityId: form.idpEntityId.trim(),
        idpSsoUrl: form.idpSsoUrl.trim(),
        idpSigningCertificates: pemList(form.idpSigningCertificates),
        spEntityId: form.spEntityId.trim(),
        acsUrl: `${serviceBaseUrl}/v1/auth/organization-sso/{connectionId}/saml/acs`,
        spPublicCertificates: pemList(form.spPublicCertificates),
        nameIdFormat: 'urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified',
        wantAssertionsSigned: true,
        wantAuthnResponseSigned: true,
        authnRequestSigned: true,
        clockSkewSeconds: 120,
      },
    };
  }
  const verifyUrl = new URL(form.verifyUrl);
  return {
    ...common,
    config: {
      verifyUrl: form.verifyUrl.trim(),
      authMode: form.verificationAuthMode,
      allowedIdentifierTypes: commaList(form.allowedIdentifierTypes),
      allowedHostnames: [verifyUrl.hostname.replace(/^\[|\]$/g, '')],
      ...(form.identifierFieldName.trim()
        ? { identifierFieldName: form.identifierFieldName.trim() }
        : {}),
      responseMapping: parseObject(form.responseMapping),
      timeoutMs: Number(form.timeoutMs),
    },
  };
}

function parseObject(value: string): Record<string, unknown> {
  const parsed = JSON.parse(value) as unknown;
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
    throw new Error('JSON object required');
  return parsed as Record<string, unknown>;
}

function commaList(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function pemList(value: string): string[] {
  return value
    .split(/(?=-----BEGIN CERTIFICATE-----)/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

function trimBase(value: string): string {
  return value.trim().replace(/\/+$/, '');
}

function serviceBaseUrl(config: Record<string, unknown>, connectionId: string): string {
  const uri = stringValue(config.redirectUri) || stringValue(config.acsUrl);
  if (!uri) return typeof window !== 'undefined' ? window.location.origin : '';
  return uri.split(`/v1/auth/organization-sso/${connectionId}/`)[0] ?? '';
}
